import React, { useState } from 'react';
import { styled, focusRing, hideMouseFocus, filterOutProps } from '@workday/canvas-kit-react/common';
import isPropValid from '@emotion/is-prop-valid';
import { borderRadius, colors } from '@workday/canvas-kit-react/tokens';
import { SystemIconCircle, SystemIconCircleSize } from '@workday/canvas-kit-react/icon';
import { userIcon } from '@workday/canvas-system-icons-web';
export var AvatarVariant;
(function (AvatarVariant) {
    AvatarVariant[AvatarVariant["Light"] = 0] = "Light";
    AvatarVariant[AvatarVariant["Dark"] = 1] = "Dark";
})(AvatarVariant || (AvatarVariant = {}));
const fadeTransition = 'opacity 150ms linear';
const StyledContainer = styled('button', {
    shouldForwardProp: prop => isPropValid(prop) && prop !== 'size',
})({
    background: colors.soap200,
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    border: 0,
    boxSizing: 'border-box',
    overflow: 'hidden',
    borderRadius: borderRadius.circle,
    '&:not([disabled])': {
        '&:focus': {
            outline: 'none',
            ...focusRing({ separation: 2 }),
        },
    },
    ...hideMouseFocus,
}, ({ size }) => ({
    height: size,
    width: size,
}), ({ onClick }) => ({
    cursor: onClick ? 'pointer' : 'default',
}));
const StyledStack = styled('span')({
    position: 'absolute',
    top: 0,
    left: 0,
}, ({ size }) => ({
    height: size,
    width: size,
}));
const StyledIcon = styled(SystemIconCircle, {
    shouldForwardProp: filterOutProps(['isImageLoaded']),
})({
    transition: fadeTransition,
}, ({ isImageLoaded }) => ({
    opacity: isImageLoaded ? 0 : 1,
}));
const StyledImage = styled('img', {
    shouldForwardProp: filterOutProps(['isLoaded', 'objectFit']),
})({
    width: '100%',
    height: '100%',
    borderRadius: borderRadius.circle,
    transition: fadeTransition,
}, ({ isLoaded, objectFit }) => ({
    opacity: isLoaded ? 1 : 0,
    objectFit,
}));
export const Avatar = React.forwardRef(({ variant = AvatarVariant.Light, size = SystemIconCircleSize.m, altText = 'Avatar', url, onClick, objectFit, ...elemProps }, ref) => {
    const [imageLoaded, setImageLoaded] = useState(false);
    const loadImage = () => {
        if (!imageLoaded) {
            setImageLoaded(true);
        }
    };
    React.useLayoutEffect(() => {
        setImageLoaded(false);
    }, [url]);
    const background = variant === AvatarVariant.Dark ? colors.blueberry400 : colors.soap300;
    return (React.createElement(StyledContainer, Object.assign({ size: size, "aria-label": altText, onClick: onClick, disabled: onClick ? false : true, ref: ref }, elemProps),
        React.createElement(StyledStack, { size: size },
            React.createElement(StyledIcon, { icon: userIcon, background: background, size: size, isImageLoaded: imageLoaded })),
        url && (React.createElement(StyledStack, { size: size },
            React.createElement(StyledImage, { src: url, alt: altText, onLoad: loadImage, isLoaded: imageLoaded, objectFit: objectFit, loading: "lazy" })))));
}); // AvatarProps and forwardRef signatures are incompatible, so we must force cast
Avatar.Variant = AvatarVariant;
Avatar.Size = SystemIconCircleSize;
